import React, { useEffect, useMemo, useState } from 'react';
import { Avatar, Flex } from 'antd';
import { faSlack } from '@fortawesome/free-brands-svg-icons';
import { faUsers } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { debounce, sortBy } from 'lodash-es';

import { trackEvent } from 'jf/analytics/Analytics';
import { RosterClient, SlackEntity, SlackTargetingClient, SlackUser } from 'jf/api';
import { useDevExTheme } from 'jf/common/themes/DevExTheme';
import { DevExEmpty } from 'jf/components/DevExEmpty';
import { DevExLoader } from 'jf/components/DevExLoader';
import { DevExSelect } from 'jf/components/DevExSelect';
import { useClientMutation, useClientQuery } from 'jf/utils/useClientQuery';

export const SlackTargetSearch: React.FC<{
  unsavedSlackTargets: SlackUser[];
  onChange: (slackTargets: SlackUser[]) => void;
  studyRef: string;
}> = (props) => {
  const { data: targetedSlackRespondents } = useClientQuery(RosterClient.getRosterTargets, {
    studyRef: props.studyRef,
    slackSelected: true,
  });

  const slackTargets = targetedSlackRespondents?.map((respondent) => respondent.slack!) || [];
  const theme = useDevExTheme();
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const debounceFiredSearchQuery = useMemo(() => debounce(setDebouncedSearchQuery, 300), []);
  const { data: slackEntities, isFetching } = useClientQuery(
    SlackTargetingClient.getSlackEntities,
    { filter: debouncedSearchQuery },
    { enabled: debouncedSearchQuery.length >= 1 }
  );
  const { mutateAsync: expandSlackEntity } = useClientMutation(
    SlackTargetingClient.expandSlackEntity
  );
  useEffect(() => {
    if (searchQuery) {
      debounceFiredSearchQuery(searchQuery);
    } else {
      debounceFiredSearchQuery.cancel();
      setDebouncedSearchQuery('');
    }
  }, [searchQuery]);

  const isSearching = searchQuery !== debouncedSearchQuery || isFetching;
  const slackEntityOptions = useMemo(() => {
    return sortBy(slackEntities, (entity) => entity.name.toLowerCase()).map((entity) => ({
      value: entity.id,
      label: (
        <Flex gap={theme.variable.spacing.sm} align="center">
          <Avatar
            shape={'square'}
            style={{ backgroundColor: theme.color.background.active }}
            size="large"
            src={entity.image ?? <FontAwesomeIcon icon={faUsers} />}
          />
          <Flex vertical>
            {entity.displayName}
            <div style={{ color: theme.color.text.secondary }}>
              {' '}
              {`${entity.type === SlackEntity.type.CHANNEL ? '#' : '@'}${entity.name}`}
            </div>
          </Flex>
        </Flex>
      ),
    }));
  }, [slackEntities]);
  return (
    <DevExSelect
      showSearch
      filterOption={() => true} // leave filtering to the BE only
      options={!isSearching ? slackEntityOptions : undefined}
      value={[]}
      onChange={(value) => {
        const entity = slackEntities?.find((entity) => entity.id === value);
        if (entity) {
          trackEvent(`survey-analyzer:target-respondent:add`, {
            id: entity.id,
            type: entity.type,
          });

          expandSlackEntity({
            requestBody: {
              entityId: entity.id,
              entityType: entity.type,
            },
          }).then((newSlackTargets) => {
            const filteredUsers = newSlackTargets.filter((n) => {
              const alreadyTargeted = slackTargets.find((t) => t.slackUserId === n.slackUserId);
              const existingUnsavedTarget = props.unsavedSlackTargets.find(
                (t) => t.slackUserId === n.slackUserId
              );
              return !alreadyTargeted && !existingUnsavedTarget;
            });
            const newTargets = [...props.unsavedSlackTargets, ...filteredUsers];
            props.onChange(newTargets);
          });
        }

        setSearchQuery('');
      }}
      placeholder="Search for @users, @groups, and #channels"
      loading={isSearching}
      notFoundContent={
        isSearching ? (
          <DevExLoader tip="Searching Slack..." />
        ) : (
          <DevExEmpty
            icon={faSlack}
            label={
              searchQuery
                ? `No slack targets found matching "${searchQuery}".`
                : 'Search for @users, @groups, and #channels in your Slack instance.'
            }
          />
        )
      }
      onSearch={setSearchQuery}
    />
  );
};
